import { Box, Chip, Container, Divider, MenuItem, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { FC, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { SurveyQuestionStats } from '../../../components/question/question-stats.component';
import {
	useGetSurveyStatsQuery,
	useGetSurveyStatsByFamilyQuery,
	useGetSurveyTemplateByIdQuery,
} from '../../../../../store/api/survey.api';
import {
	FamilyAnswerStatsDto,
	QuestionStatsDto,
	SurveyQuestionTypeEnum,
	SurveyStatsViewByEnum,
} from '@families-link/shared';
import { SurveyRowQuestionStats } from '../../../components/question/question-row-stats.component';
import { DistrictRoutes } from '../../../../district/constants';
import { Controller } from 'react-hook-form';
import CoreCalendar from '../../../../_core/components/_ui/core-date-picker/core-calendar/core-calendar.component';
import dayjs from 'dayjs';
import { useGetDistrictListQuery } from '../../../../../store/api/district.api';
import { CoreAutoComplete, AutocompleteTextField } from '../../../../_core/components/_ui/core-autocomplete';
import { CoreOverflowTypographyTooltip } from '../../../../_core/components/_ui/core-overflow-tooltip';
import { ChipCloseIcon } from '../../../../_core/components/icons';
import { chipDeleteIconStyles } from '../../../../_core/styles';
import { ProfileRoutes } from '../../../../profile/constants';
import { CoreLoadingWall } from '../../../../_core/components/_ui/core-loading-wall';
import { useDurationLocalSurveyFilter } from '../../../../_core/hooks/use-duration-local-survey.hook';
import { useDistrictSurveyLocalFilter } from '../../../../_core/hooks/use-district-filter-survey-local.hook';
import { usePaginatedData, useSortingParams } from '../../../../_core/hooks';
import { FamiliesResponsesTable } from '../../../components/families-responses-table/families-responses-table.component';
import CoreCalendarAdvanced from '../../../../_core/components/_ui/core-date-picker/core-calendar/core-calendar-advanced.component';

const toggleButtonStyles = {
	borderRadius: '12px',
	border: '1px solid var(--Stroke-gray, #DCDFE7)',
	textTransform: 'none',
	fontWeight: 500,
	fontSize: '14px',
	color: '#000',
	'& > *': {
		fontWeight: 500,
	},
	'&.Mui-selected': {
		backgroundColor: 'var(--secondary-action-button, #3C8DBC)',
		color: '#fff',
		'&:hover': {
			backgroundColor: 'var(--secondary-action-button, #3C8DBC)',
		},
		'& > *': {
			fontWeight: 500,
		},
	},
};

export type SurveyStatisticPageProps = {
	isAdminRoute: boolean;
	currentDistrictId?: string | null;
};
export const SurveyStatisticPage: FC<SurveyStatisticPageProps> = ({ isAdminRoute, currentDistrictId }) => {
	const [t] = useTranslation();
	const navigate = useNavigate();

	const { surveyTemplateId } = useParams();
	const { data: districtList } = useGetDistrictListQuery();
	const { data: surveyTemplate } = useGetSurveyTemplateByIdQuery(surveyTemplateId!);

	const {
		name: nameDuration,
		control: controlDuration,
		transformDuration,
		currentDuration,
	} = useDurationLocalSurveyFilter();

	const {
		register: registerDistrict,
		getValues: getValuesDistrict,
		control: controlDistrict,
		districtIds,
		districts,
	} = useDistrictSurveyLocalFilter();

	const [viewBy, setViewBy] = useState<SurveyStatsViewByEnum>(SurveyStatsViewByEnum.Question);
	const { sortingArray, sortingParams, handleSortingChange } = useSortingParams();

	const { data: questionStatsData, isLoading: isLoadingQuestion } = useGetSurveyStatsQuery(
		{
			templateId: surveyTemplateId,
			district: currentDistrictId,
			start: transformDuration.start
				? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
			end: transformDuration.start ? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '',
			districts: districtIds?.join(','),
		},
		{ skip: !surveyTemplateId || viewBy !== SurveyStatsViewByEnum.Question }
	);

	const { requestObject, meta, handleResetPage, itemsList, fetchMore } = usePaginatedData<FamilyAnswerStatsDto>(
		useGetSurveyStatsByFamilyQuery,
		{
			...sortingParams,
			templateId: surveyTemplateId,
			district: currentDistrictId,
			start: transformDuration.start
				? dayjs(transformDuration?.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
			end: transformDuration.start ? dayjs(transformDuration?.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ') : '',
			districts: districtIds?.join(','),
		},
		{ skip: !surveyTemplateId || viewBy !== SurveyStatsViewByEnum.Family }
	);

	const isFirstRender = useRef(true);

	const { isFetching, isLoading } = requestObject;

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;

			return;
		}
		if (viewBy === SurveyStatsViewByEnum.Family) {
			handleResetPage();
		}
	}, [viewBy, sortingArray, transformDuration, districts, handleResetPage]);

	const handleOpenView = (questionStat: QuestionStatsDto) => {
		if (isAdminRoute) {
			navigate(
				generatePath(ProfileRoutes.SurveyTemplates.QuestionStats, {
					surveyTemplateId: surveyTemplateId,
					questionId: questionStat.questionId,
				})
			);
		} else {
			navigate(
				generatePath(DistrictRoutes.SurveyTemplates.QuestionStats, {
					surveyTemplateId: surveyTemplateId,
					questionId: questionStat.questionId,
				})
			);
		}
	};

	const handleOpenViewFamily = (familyAnswer: FamilyAnswerStatsDto) => {
		if (isAdminRoute) {
			navigate(
				generatePath(ProfileRoutes.SurveyTemplates.FamilyStats, {
					surveyTemplateId: surveyTemplateId,
					phoneNumber: familyAnswer.phoneNumber,
				}),
				{ state: { familyAnswer } }
			);
		} else {
			navigate(
				generatePath(DistrictRoutes.SurveyTemplates.FamilyStats, {
					surveyTemplateId: surveyTemplateId,
					phoneNumber: familyAnswer.phoneNumber,
				}),
				{ state: { familyAnswer } }
			);
		}
	};

	return (
		<Container
			maxWidth={false}
			sx={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: '16px', gap: 2 }}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography sx={{ fontSize: '14px', fontWeight: 500 }}>View by:</Typography>
					<ToggleButtonGroup
						sx={{
							display: 'flex',
							width: '226px',
							height: '40px',
							borderRadius: '12px',
						}}
						value={viewBy}
						exclusive
						onChange={(_, val) => val && setViewBy(val)}
						size="small"
					>
						<ToggleButton value="question" sx={{ ...toggleButtonStyles, flex: 1 }}>
							Question
						</ToggleButton>
						<ToggleButton value="family" sx={{ ...toggleButtonStyles, flex: 1 }}>
							Family
						</ToggleButton>
					</ToggleButtonGroup>
					<Divider orientation="vertical" flexItem />
					<Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
						Total responds: {(viewBy === 'question' ? questionStatsData : requestObject?.data)?.totalResponses}
					</Typography>
				</Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: 1,
						alignItems: 'center',
					}}
				>
					{isAdminRoute && (
						<Box sx={{ maxWidth: '270px', maxHeight: '48px' }}>
							<CoreAutoComplete
								multiple={true}
								{...registerDistrict('districts')}
								control={controlDistrict}
								controlSx={{ minWidth: '190px' }}
								options={districtList || []}
								isFilter
								getOptionLabel={(option) => option.name || ''}
								selectOnFocus={true}
								isOptionEqualToValue={(option, value) => option._id === value._id}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValuesDistrict('districts')?.length ? t('common.filter-district') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
											'& .MuiInputBase-input': {
												height: '100%',
												padding: '0',
											},
											'& .MuiOutlinedInput-root': {
												'&.Mui-focused fieldset': {
													borderColor: (theme) => theme.colors.blue,
												},
											},
										}}
									/>
								)}
								renderOption={(props, option) => (
									<MenuItem {...props} key={option._id}>
										<CoreOverflowTypographyTooltip>{option.name}</CoreOverflowTypographyTooltip>
									</MenuItem>
								)}
								renderTags={(value, getTagProps) => {
									const count = value.length - 1;

									return [
										value[0] && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: 0 })}
												label={value[0]?.name}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
										count > 0 && (
											<Chip
												sx={chipDeleteIconStyles}
												{...getTagProps({ index: count })}
												label={`+${count}`}
												deleteIcon={<ChipCloseIcon />}
											/>
										),
									];
								}}
							/>
						</Box>
					)}
					<Box sx={{ minWidth: '200px', maxWidth: '270px', maxHeight: '48px' }}>
						<Controller
							name={nameDuration}
							control={controlDuration}
							render={({ field: { value, ...fieldWithoutValue } }) => (
								<CoreCalendarAdvanced
									{...fieldWithoutValue}
									selectsRange
									inline={false}
									startDate={currentDuration?.length ? currentDuration[0] : null}
									endDate={currentDuration?.length ? currentDuration[1] : null}
									onChange={(dates: [Date | null, Date | null]) => {
										fieldWithoutValue.onChange(dates);
									}}
									onKeyDown={(e) => {
										e.preventDefault();
									}}
									onClear={() => {
										fieldWithoutValue.onChange([null, null]);
									}}
								/>
							)}
						/>
					</Box>
				</Box>
			</Box>

			{(viewBy === SurveyStatsViewByEnum.Question && isLoadingQuestion) ||
			(viewBy === SurveyStatsViewByEnum.Family && isLoading) ? (
				<CoreLoadingWall />
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
					{viewBy === SurveyStatsViewByEnum.Question && questionStatsData?.stats && (
						<>
							{questionStatsData?.stats
								.filter(
									(q) => q.type === SurveyQuestionTypeEnum.Options || q.type === SurveyQuestionTypeEnum.MultiSelect
								)
								.map((questionStat) => (
									<SurveyQuestionStats
										key={questionStat.questionId}
										questionStat={questionStat}
										onView={handleOpenView}
									/>
								))}

							{questionStatsData?.stats
								.filter(
									(q) => q.type !== SurveyQuestionTypeEnum.Options && q.type !== SurveyQuestionTypeEnum.MultiSelect
								)
								.map((questionStat) => (
									<SurveyRowQuestionStats
										key={questionStat.questionId}
										questionStat={questionStat}
										onView={handleOpenView}
									/>
								))}
						</>
					)}

					{viewBy === SurveyStatsViewByEnum.Family && (
						<FamiliesResponsesTable
							data={itemsList ?? []}
							loading={isFetching || isLoading}
							hasMore={meta?.hasNextPage}
							fetchMore={fetchMore}
							sorting={sortingArray}
							onSortingChange={handleSortingChange}
							onView={handleOpenViewFamily}
						/>
					)}
				</Box>
			)}
		</Container>
	);
};
